import { useEffect, useMemo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import SortIcon from "src/components/ui-icons/table/SortIcon";
import {
  myDocumentsListName,
  versionsByDocumentListName,
} from "src/api/config";
import { Option } from "src/components/ui/TGSelect";
import { getItemsRequest } from "src/modules/spItems/api";
import { modifiedItemsState } from "src/modules/spItems/selectors";
import { twMerge } from "tailwind-merge";
import { CollectionOption } from "./ChangeResponsibleReviewModal";

type SortDirection = "asc" | "desc" | "default";
type SortField = "code" | "title" | "collection" | "department" | "type";

export type ChangeResponsibleDocument = {
  id: number;
  selectionKey: string;
  listName: typeof myDocumentsListName | typeof versionsByDocumentListName;
  mainDocumentId?: number;
  documentId?: string;
  code: string;
  title: string;
  collection: string;
  department: string;
  type: string;
};

type SelectDocumentsModalProps = {
  selectedCollections: CollectionOption[];
  currentResponsible: Option[];
  selectedDocuments: string[];
  submitting: boolean;
  onChangeSelectedDocuments: (ids: string[]) => void;
  onBack: () => void;
  onSubmit: (documents: ChangeResponsibleDocument[]) => void;
};

const tableColumns: { key: SortField; title: string; className: string }[] = [
  { key: "code", title: "Код", className: "w-[23%]" },
  { key: "title", title: "Название документа", className: "w-[24%]" },
  { key: "collection", title: "Коллекция", className: "w-[20%]" },
  { key: "department", title: "Подразделение/ Функция", className: "w-[24%]" },
  { key: "type", title: "Тип", className: "w-[9%]" },
];

const getLookupTitle = (value: any) => {
  if (!value) {
    return "";
  }

  return (
    value.Title || value.Name || value.name || value.get_lookupValue?.() || ""
  );
};

const getSelectionKey = (
  listName: typeof myDocumentsListName | typeof versionsByDocumentListName,
  id: number,
) => `${listName}:${id}`;

const getLookupId = (value: any) =>
  value?.ID || value?.id || value?.get_lookupId?.();

const getDocumentLink = (document: ChangeResponsibleDocument) => {
  if (
    document.listName === versionsByDocumentListName &&
    document.mainDocumentId
  ) {
    return `documents?documentCard=${document.mainDocumentId}&version=${document.id}`;
  }

  return `documents?documentCard=${document.id}`;
};

const mapDocument = (
  document: any,
  listName: typeof myDocumentsListName | typeof versionsByDocumentListName,
): ChangeResponsibleDocument => ({
  id: Number(document.ID),
  selectionKey: getSelectionKey(listName, Number(document.ID)),
  listName,
  mainDocumentId: Number(getLookupId(document.MainDocument)) || undefined,
  documentId: document.documentId,
  code: [document.Code, document.Index].filter(Boolean).join("."),
  title: document.Title || "",
  collection: getLookupTitle(document.Collection),
  department: getLookupTitle(document.Department),
  type: getLookupTitle(document.DocType),
});

function SelectDocumentsModal({
  selectedCollections,
  currentResponsible,
  selectedDocuments,
  submitting,
  onChangeSelectedDocuments,
  onBack,
  onSubmit,
}: SelectDocumentsModalProps) {
  const [documents, setDocuments] = useState<ChangeResponsibleDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("default");

  const currentResponsibleId = currentResponsible[0]?.value?.Id;
  const selectedCollectionIds = useMemo(
    () =>
      selectedCollections.map((collection) => collection.id).filter(Boolean),
    [selectedCollections],
  );

  useEffect(() => {
    async function fetchDocuments() {
      if (!currentResponsibleId || selectedCollectionIds.length === 0) {
        setDocuments([]);
        return;
      }

      setLoading(true);

      try {
        const loadedDocuments: ChangeResponsibleDocument[] = [];

        const filters = {
          ResponsibleForRevision: [currentResponsibleId],
          Collection: selectedCollectionIds,
        };
        const [documentsResponse, versionsResponse] = await Promise.all([
          getItemsRequest({
            listName: myDocumentsListName,
            filters,
          }),
          getItemsRequest({
            listName: versionsByDocumentListName,
            filters,
          }),
        ]);

        const mapResponseDocuments = (
          response: typeof documentsResponse | typeof versionsResponse,
          listName:
            | typeof myDocumentsListName
            | typeof versionsByDocumentListName,
        ) => {
          const documentIdsById = new Map(
            response.items.map((document: any) => [
              Number(document.ID),
              document.documentId,
            ]),
          );

          return modifiedItemsState(response.items, listName).map(
            (document) => ({
              ...mapDocument(document, listName),
              documentId: documentIdsById.get(Number(document.ID)),
            }),
          );
        };

        loadedDocuments.push(
          ...mapResponseDocuments(documentsResponse, myDocumentsListName),
          ...mapResponseDocuments(versionsResponse, versionsByDocumentListName),
        );
        setDocuments(loadedDocuments);
        onChangeSelectedDocuments(
          selectedDocuments.filter((selectionKey) =>
            loadedDocuments.some(
              (document) => document.selectionKey === selectionKey,
            ),
          ),
        );
      } catch (error) {
        console.error("Error fetching change responsible documents:", error);
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    }

    fetchDocuments();
  }, [currentResponsibleId, selectedCollectionIds.join(",")]);

  const sortedDocuments = useMemo(() => {
    if (!sortField || sortDirection === "default") {
      return documents;
    }

    return [...documents].sort((left, right) => {
      const leftValue = left[sortField] || "";
      const rightValue = right[sortField] || "";
      const compare = leftValue.localeCompare(rightValue, "ru", {
        sensitivity: "base",
      });

      return sortDirection === "asc" ? compare : -compare;
    });
  }, [documents, sortDirection, sortField]);

  const selectedDocumentsSet = useMemo(
    () => new Set(selectedDocuments),
    [selectedDocuments],
  );
  const isAllSelected =
    documents.length > 0 &&
    documents.every((document) =>
      selectedDocumentsSet.has(document.selectionKey),
    );

  const handleSort = (field: SortField) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDirection("asc");
      return;
    }

    setSortDirection((direction) => {
      if (direction === "default") {
        return "asc";
      }

      if (direction === "asc") {
        return "desc";
      }

      return "default";
    });
  };

  const toggleDocument = (selectionKey: string) => {
    if (selectedDocumentsSet.has(selectionKey)) {
      onChangeSelectedDocuments(
        selectedDocuments.filter((key) => key !== selectionKey),
      );
      return;
    }

    onChangeSelectedDocuments([...selectedDocuments, selectionKey]);
  };

  const toggleAllDocuments = () => {
    if (isAllSelected) {
      onChangeSelectedDocuments([]);
      return;
    }

    onChangeSelectedDocuments(
      documents.map((document) => document.selectionKey),
    );
  };

  const selectedFullDocuments = documents.filter((document) =>
    selectedDocumentsSet.has(document.selectionKey),
  );

  return (
    <div className="fixed inset-0 z-[100] flex justify-end bg-black/20">
      <div className="relative flex h-screen w-[80vw] flex-col bg-white px-9 py-10 shadow-xl">
        <button
          type="button"
          className="absolute right-[calc(100%+16px)] top-2 flex items-center justify-center"
          onClick={onBack}
        >
          <XMarkIcon
            className="h-[36px] w-[36px] text-white"
            strokeWidth={1.5}
          />
        </button>

        <header className="mb-7">
          <div className="flex items-center gap-5">
            <h2 className="text-[28px] leading-[34px] font-bold text-[#111928]">
              Выберите документы
            </h2>
            <span className="rounded-md bg-[#EEF2FF] px-3 py-2 text-[14px] leading-[18px] font-bold text-[#111928]">
              Выбрано: {selectedDocuments.length}
            </span>
          </div>
          <p className="mt-7 text-[16px] leading-[22px] text-[#111928]">
            Найдено документов: {loading ? "..." : documents.length}
          </p>
        </header>

        <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-[#E5E7EB] shadow-sm">
          <div className="h-full overflow-y-auto px-6 pb-6 scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB]">
            <div className="sticky top-0 z-[1] flex h-[52px] items-center border-b border-[#DFE4EA] bg-white text-[14px] leading-[20px] text-[#111928]">
              <div className="w-[36px] shrink-0">
                <input
                  type="checkbox"
                  className="h-[18px] w-[18px] rounded border-[#DFE4EA] text-[#012169]"
                  checked={isAllSelected}
                  onChange={toggleAllDocuments}
                  disabled={documents.length === 0}
                  aria-label="Выбрать все документы"
                />
              </div>
              {tableColumns.map((column) => (
                <button
                  key={column.key}
                  type="button"
                  className={twMerge(
                    "flex items-center gap-2 text-left",
                    column.className,
                  )}
                  onClick={() => handleSort(column.key)}
                >
                  <span>{column.title}</span>
                  <SortIcon
                    sortDirection={
                      sortField === column.key ? sortDirection : "default"
                    }
                  />
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex h-[160px] items-center justify-center text-[14px] text-[#637381]">
                Загрузка...
              </div>
            ) : sortedDocuments.length === 0 ? (
              <div className="flex h-[160px] items-center justify-center text-[14px] text-[#637381]">
                Документы не найдены
              </div>
            ) : (
              sortedDocuments.map((document) => (
                <div
                  key={document.selectionKey}
                  className="flex min-h-[44px] items-center border-b border-[#DFE4EA] text-[14px] leading-[20px] text-[#637381]"
                >
                  <div className="w-[36px] shrink-0">
                    <input
                      type="checkbox"
                      className="h-[18px] w-[18px] rounded border-[#DFE4EA] text-[#012169]"
                      checked={selectedDocumentsSet.has(document.selectionKey)}
                      onChange={() => toggleDocument(document.selectionKey)}
                      aria-label={`Выбрать документ ${document.code}`}
                    />
                  </div>
                  <div className="w-[23%] pr-4">{document.code}</div>
                  <div className="w-[24%] pr-4 font-medium text-[#012169]">
                    <a
                      href={getDocumentLink(document)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {document.title}
                    </a>
                  </div>
                  <div className="w-[20%] pr-4">{document.collection}</div>
                  <div className="w-[24%] pr-4">{document.department}</div>
                  <div className="w-[9%]">{document.type}</div>
                </div>
              ))
            )}
          </div>
        </div>

        <footer className="mt-9 flex items-center justify-between">
          <button
            type="button"
            className="h-[42px] min-w-[88px] rounded-md border border-[#DFE4EA] bg-white px-5 text-[14px] leading-[20px] font-medium text-[#012169] shadow-sm"
            onClick={onBack}
          >
            Назад
          </button>
          {documents.length > 0 && (
            <button
              type="button"
              className={twMerge(
                "h-[42px] min-w-[195px] rounded-md px-5 text-[14px] leading-[20px] font-medium",
                selectedDocuments.length === 0 || loading || submitting
                  ? "cursor-not-allowed bg-[#E5E7EB] text-[#8899A8]"
                  : "bg-[#012169] text-white",
              )}
              disabled={selectedDocuments.length === 0 || loading || submitting}
              onClick={() => onSubmit(selectedFullDocuments)}
            >
              Изменить выбранные
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

export default SelectDocumentsModal;
